import { getFormattedAddress } from '@sales-center/common'

import {
  DataItem,
  ButtonWithTooltip,
  PhoneLink,
} from '@sales-center/components'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { IContactSearchModel } from '@user/query/useContactSearch'
import * as React from 'react'
import { FC, useMemo } from 'react'
import { matchedCardStyles } from './styling'
import Stack from '@mui/material/Stack'

interface Props {
  contact: IContactSearchModel
  disabled: boolean
  disabledReason: string
  onAddContactClick: (contactId: number) => void
}

export const MatchedContactCard: FC<Props> = (props: Props) => {
  const { contact, disabled, disabledReason, onAddContactClick } = props

  const formattedAddress = useMemo(
    () =>
      getFormattedAddress(
        contact.street1,
        contact.street2,
        contact.city,
        contact.stateAbbreviation,
        contact.postalCode,
      ) || <>&mdash;</>,
    [
      contact.city,
      contact.postalCode,
      contact.stateAbbreviation,
      contact.street1,
      contact.street2,
    ],
  )

  const primaryEmail = useMemo(
    () =>
      contact.emailAddresses.find((x) => x.isPrimary)?.emailAddress || (
        <>&mdash;</>
      ),
    [contact.emailAddresses],
  )

  return (
    <Card sx={matchedCardStyles.card} elevation={2}>
      <CardContent sx={matchedCardStyles.content}>
        <Grid container>
          <Grid container item>
            <Grid item xs={6}>
              <Typography sx={matchedCardStyles.heading} variant="h3">
                {contact.firstName || contact.lastName
                  ? `${contact.firstName} ${contact.lastName}`
                  : contact.company}
              </Typography>
            </Grid>
            <Grid item xs={6} style={{ textAlign: 'right' }}>
              <ButtonWithTooltip
                color="primary"
                disabled={disabled}
                showTooltip={disabled}
                tooltipTitle={disabled ? disabledReason : undefined}
                onClick={() => onAddContactClick(contact.id)}
                variant="contained"
                dataTestId="addMatchedContactBtn"
              >
                Add Contact
              </ButtonWithTooltip>
            </Grid>
          </Grid>
          <DataItem title="Address:" xs={12}>
            {formattedAddress}
          </DataItem>
        </Grid>
        <Stack>
          <DataItem title="Main Phone:" xs={6} mt={8}>
            <PhoneLink
              phone={contact.phoneNumber}
              phoneExt={contact.phoneExt}
              dataTestId="matchedContactPhoneLink"
            />
          </DataItem>
          <DataItem
            isEmpty={!contact.emailAddresses || !primaryEmail}
            title="Primary Email:"
            xs={6}
            mt={8}
          >
            <a
              href={`mailto:${primaryEmail}`}
              target="_blank"
              style={{ overflowWrap: 'break-word' }}
              rel="noreferrer"
              data-test-id="matchedContactEmailLink"
            >
              {primaryEmail}
            </a>
          </DataItem>
        </Stack>
      </CardContent>
    </Card>
  )
}
